/**
 * BillingRollup — aggregated billing summary across all accounts for a client.
 *
 * Props:
 *   accounts  — list of AccountRow objects (snake_case from API)
 *   clientId? — when provided, fetches real data from the billing-rollup API
 *
 * Displays:
 *   - Per-account breakdown (from API): account name, Stripe customer ID,
 *     total paid, open invoices, overdue invoices
 *   - Status breakdown by account status
 *   - Aggregated monthly billing (falls back to monthly_total_cents when no API data)
 *
 * Phase 15: When clientId is provided, real billing data is fetched from
 * /api/v1/billing/clients/:clientId/billing-rollup.
 */

import type { AccountRow } from '@1klick/types'
import { useClientBillingRollup } from '@/hooks/useBilling'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCents(cents: number, currency = 'COP'): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
  }).format(cents / 100)
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Activos',
  pending: 'Pendientes',
  inactive: 'Inactivos',
  suspended: 'Suspendidos',
}

const STATUS_DOT: Record<string, string> = {
  active: 'bg-green-500',
  pending: 'bg-yellow-400',
  inactive: 'bg-gray-400',
  suspended: 'bg-red-400',
}

// ── Component ─────────────────────────────────────────────────────────────────

interface BillingRollupProps {
  accounts: AccountRow[]
  clientId?: string
}

export function BillingRollup({ accounts, clientId }: BillingRollupProps) {
  // Fetch real billing data when clientId is available
  const { data: rollupData, isLoading: rollupLoading } = useClientBillingRollup(clientId ?? '')

  const accountCount = accounts.length

  // Count by status (from accounts list — always available)
  const countByStatus = accounts.reduce<Record<string, number>>((acc, a) => {
    acc[a.status] = (acc[a.status] ?? 0) + 1
    return acc
  }, {})

  // Determine if we have real API data to show
  const hasRealData = !!clientId && !rollupLoading && rollupData && rollupData.length > 0

  // Fallback: sum monthly_total_cents from accounts
  const totalMonthly = accounts.reduce((sum, a) => sum + (a.monthly_total_cents ?? 0), 0)
  const hasFallbackBillingData = totalMonthly > 0

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-base font-semibold text-gray-900">Resumen de facturacion</h2>

      {accountCount === 0 ? (
        <p className="text-sm text-gray-500">Sin cuentas registradas</p>
      ) : (
        <div className="space-y-4">
          {/* ── Real billing data from API ── */}
          {clientId && rollupLoading && (
            <p className="text-xs text-gray-400">Cargando datos de facturacion...</p>
          )}

          {hasRealData && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Detalle por cuenta
              </h3>
              <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
                {rollupData!.map((row) => (
                  <div key={row.account_id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    {/* Account name */}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">{row.account_name}</p>
                      <p className="mt-0.5 font-mono text-xs text-gray-400">
                        {row.stripe_customer_id
                          ? row.stripe_customer_id.slice(0, 16) + '…'
                          : '--'}
                      </p>
                    </div>

                    {/* Total paid */}
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Total pagado</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCents(row.total_paid_cents)}
                      </p>
                    </div>

                    {/* Open invoices */}
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Abiertas</p>
                      <p className="text-sm font-medium text-blue-700">{row.open_invoices}</p>
                    </div>

                    {/* Overdue invoices */}
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Vencidas</p>
                      <p
                        className={`text-sm font-medium ${
                          row.overdue_invoices > 0 ? 'text-red-600' : 'text-gray-500'
                        }`}
                      >
                        {row.overdue_invoices > 0 ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
                            {row.overdue_invoices}
                          </span>
                        ) : (
                          row.overdue_invoices
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Fallback: monthly total from accounts cache ── */}
          {!hasRealData && !rollupLoading && (
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900">
                {hasFallbackBillingData ? formatCents(totalMonthly) : '—'}
              </span>
              <span className="text-sm text-gray-500">/ mes</span>
            </div>
          )}

          {!hasRealData && !rollupLoading && !hasFallbackBillingData && clientId && (
            <p className="text-xs text-gray-400">
              Sin datos de facturacion. Configure Stripe para este cliente.
            </p>
          )}

          {/* Fallback without clientId (legacy behavior) */}
          {!clientId && !hasFallbackBillingData && (
            <p className="text-xs text-gray-400">
              Datos de facturacion disponibles tras configurar Stripe (Fase 15)
            </p>
          )}

          {/* ── Account count ── */}
          <div className="flex items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-sm font-medium text-gray-700">{accountCount}</span>
            <span className="text-sm text-gray-500">
              {accountCount === 1 ? 'cuenta' : 'cuentas'} en total
            </span>
          </div>

          {/* ── Status breakdown ── */}
          {Object.entries(countByStatus).length > 0 && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {Object.entries(countByStatus).map(([status, count]) => (
                <div
                  key={status}
                  className="flex items-center gap-1.5 rounded-md bg-gray-50 px-3 py-2"
                >
                  <span className={`h-2 w-2 rounded-full ${STATUS_DOT[status] ?? 'bg-gray-400'}`} />
                  <span className="text-xs font-medium text-gray-700">{count}</span>
                  <span className="text-xs text-gray-500">{STATUS_LABELS[status] ?? status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
